import React, { useState, useRef } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  Text,
  Box,
  Input,
  Alert,
  AlertIcon,
  Progress,
  Badge,
  useToast,
  List,
  ListItem,
  ListIcon,
  Divider,
} from "@chakra-ui/react";
import { FiUpload, FiFile, FiCheck, FiX, FiAlertCircle } from "react-icons/fi";
import preBoardingService from "../services/preBoardingService";

const DocumentUploadModal = ({
  isOpen,
  onClose,
  packetId,
  documentIndex,
  document,
  onSuccess,
}) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);

  const fileInputRef = useRef(null);
  const toast = useToast();

  const allowedTypes = [
    "application/pdf",
    "image/jpeg",
    "image/png",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ];

  const maxSize = 5 * 1024 * 1024; // 5MB

  const handleFileSelect = (file) => {
    if (!file) return;

    // Validate file type
    if (!allowedTypes.includes(file.type)) {
      toast({
        title: "Invalid File Type",
        description: "Please upload PDF, JPG, PNG, DOC, or DOCX files only",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Validate file size
    if (file.size > maxSize) {
      toast({
        title: "File Too Large",
        description: "Please upload files smaller than 5MB",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setSelectedFile(file);
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    handleFileSelect(file);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast({
        title: "No File Selected",
        description: "Please select a file to upload",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setUploading(true);
      setUploadProgress(0);

      // Simulate progress for better UX
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);

      await preBoardingService.uploadDocument(
        packetId,
        documentIndex,
        selectedFile
      );

      clearInterval(progressInterval);
      setUploadProgress(100);

      toast({
        title: "Success",
        description: "Document uploaded successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      // Wait a moment to show 100% progress
      setTimeout(() => {
        onSuccess();
        handleClose();
      }, 500);
    } catch (error) {
      toast({
        title: "Upload Failed",
        description:
          error.response?.data?.message || "Failed to upload document",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleClose = () => {
    if (!uploading) {
      setSelectedFile(null);
      setUploadProgress(0);
      onClose();
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const getFileIcon = (type) => {
    if (type.includes("pdf")) return "📄";
    if (type.includes("image")) return "🖼️";
    if (type.includes("word") || type.includes("document")) return "📝";
    return "📁";
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="lg"
      closeOnOverlayClick={!uploading}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Upload Document</ModalHeader>
        {!uploading && <ModalCloseButton />}

        <ModalBody>
          <VStack spacing={4} align="stretch">
            {/* Document Info */}
            <Box>
              <Text fontWeight="bold" mb={2}>
                {document?.documentType?.name}
              </Text>
              {document?.documentType?.description && (
                <Text fontSize="sm" color="gray.600" mb={2}>
                  {document.documentType.description}
                </Text>
              )}
              {document?.documentType?.isMandatory && (
                <Badge colorScheme="red" mb={2}>
                  Required Document
                </Badge>
              )}
              {document?.rejectionReason && (
                <Alert status="warning" size="sm" mb={2}>
                  <AlertIcon />
                  <Box>
                    <Text fontWeight="bold">Previous Rejection:</Text>
                    <Text fontSize="sm">{document.rejectionReason}</Text>
                  </Box>
                </Alert>
              )}
            </Box>

            <Divider />

            {/* File Upload Area */}
            <Box>
              <Text fontWeight="medium" mb={3}>
                Select or drag file to upload
              </Text>

              <Box
                border="2px dashed"
                borderColor={dragActive ? "blue.300" : "gray.300"}
                borderRadius="lg"
                p={8}
                textAlign="center"
                bg={dragActive ? "blue.50" : "gray.50"}
                cursor="pointer"
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                transition="all 0.2s"
                _hover={{
                  borderColor: "blue.400",
                  bg: "blue.50",
                }}
              >
                <VStack spacing={3}>
                  <FiUpload size={32} color="gray" />
                  <VStack spacing={1}>
                    <Text fontWeight="medium">
                      {dragActive
                        ? "Drop file here"
                        : "Click to select file or drag here"}
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      PDF, JPG, PNG, DOC, DOCX (max 5MB)
                    </Text>
                  </VStack>
                </VStack>
              </Box>

              <Input
                type="file"
                ref={fileInputRef}
                onChange={handleFileInputChange}
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                display="none"
              />
            </Box>

            {/* Selected File Info */}
            {selectedFile && (
              <Box p={4} border="1px" borderColor="gray.200" borderRadius="lg">
                <HStack justify="space-between">
                  <HStack>
                    <Text fontSize="xl">{getFileIcon(selectedFile.type)}</Text>
                    <VStack align="start" spacing={0}>
                      <Text fontWeight="medium" fontSize="sm">
                        {selectedFile.name}
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        {formatFileSize(selectedFile.size)}
                      </Text>
                    </VStack>
                  </HStack>
                  <Button
                    size="sm"
                    variant="ghost"
                    colorScheme="red"
                    onClick={() => setSelectedFile(null)}
                    isDisabled={uploading}
                  >
                    <FiX />
                  </Button>
                </HStack>
              </Box>
            )}

            {/* Upload Progress */}
            {uploading && (
              <Box>
                <Text mb={2} fontSize="sm" fontWeight="medium">
                  Uploading... {uploadProgress}%
                </Text>
                <Progress value={uploadProgress} colorScheme="blue" size="md" />
              </Box>
            )}

            {/* Upload Guidelines */}
            <Alert status="info" size="sm">
              <AlertIcon />
              <Box>
                <Text fontWeight="bold" fontSize="sm">
                  Upload Guidelines:
                </Text>
                <List fontSize="xs" mt={1} spacing={0}>
                  <ListItem>
                    <ListIcon as={FiCheck} color="green.500" />
                    Ensure document is clear and legible
                  </ListItem>
                  <ListItem>
                    <ListIcon as={FiCheck} color="green.500" />
                    Use original documents when possible
                  </ListItem>
                  <ListItem>
                    <ListIcon as={FiCheck} color="green.500" />
                    File size should be under 5MB
                  </ListItem>
                  <ListItem>
                    <ListIcon as={FiAlertCircle} color="orange.500" />
                    Documents will be reviewed by HR team
                  </ListItem>
                </List>
              </Box>
            </Alert>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button
            variant="ghost"
            mr={3}
            onClick={handleClose}
            isDisabled={uploading}
          >
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleUpload}
            isDisabled={!selectedFile || uploading}
            isLoading={uploading}
            loadingText="Uploading..."
            leftIcon={<FiUpload />}
          >
            Upload Document
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default DocumentUploadModal;
